/**
 * ActiveProjectContext — the currently selected project.
 *
 * Sources of truth (in order):
 *   1. localStorage (immediate paint, no flicker)
 *   2. /api/settings.active_project_id (durable, cross-device)
 *
 * Writes flow both ways: PATCH /api/settings and update localStorage.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { getSettings, patchSettings } from "@/lib/settings";
import { listProjects } from "@/lib/projects";

const KEY = "qoryn.activeProjectId";
const ActiveProjectContext = createContext(null);

export function ActiveProjectProvider({ children }) {
    const { user } = useAuth();
    const [projects, setProjects] = useState([]);
    const [activeId, setActiveIdState] = useState(() => {
        try { return localStorage.getItem(KEY); } catch { return null; }
    });
    const [loading, setLoading] = useState(true);
    const [refreshTick, setRefreshTick] = useState(0);

    // Load projects + settings after auth resolves.
    useEffect(() => {
        if (!user) { setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        Promise.all([listProjects(), getSettings()])
            .then(([projs, settings]) => {
                if (cancelled) return;
                setProjects(projs);
                // Prefer server value over stale localStorage.
                if (settings?.active_project_id) {
                    setActiveIdState(settings.active_project_id);
                    try { localStorage.setItem(KEY, settings.active_project_id); } catch { /* noop */ }
                } else if (activeId && !projs.find((p) => p.id === activeId)) {
                    // Local id no longer valid.
                    setActiveIdState(null);
                    try { localStorage.removeItem(KEY); } catch { /* noop */ }
                }
            })
            .catch((e) => console.warn("[active-project] load failed", e))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [user, refreshTick]);

    const setActiveId = useCallback(async (id) => {
        setActiveIdState(id);
        try {
            if (id) localStorage.setItem(KEY, id);
            else localStorage.removeItem(KEY);
        } catch { /* noop */ }
        try {
            await patchSettings({ active_project_id: id ?? "" });
        } catch (e) {
            console.warn("[active-project] patchSettings failed", e);
        }
    }, []);

    const refresh = useCallback(() => setRefreshTick((n) => n + 1), []);

    const activeProject = useMemo(
        () => projects.find((p) => p.id === activeId) ?? null,
        [projects, activeId],
    );

    const value = useMemo(
        () => ({ projects, activeId, activeProject, setActiveId, refresh, loading }),
        [projects, activeId, activeProject, setActiveId, refresh, loading],
    );
    return <ActiveProjectContext.Provider value={value}>{children}</ActiveProjectContext.Provider>;
}

export function useActiveProject() {
    const ctx = useContext(ActiveProjectContext);
    if (!ctx) throw new Error("useActiveProject must be used inside <ActiveProjectProvider>");
    return ctx;
}
